import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────
//  Panel admin: roster por semana de campamento. Junta en una sola
//  vista todas las reservas de cada semana, vengan de la web (MotoPress),
//  de inscripciones nativas en reservas.aldealix.org o de altas manuales.
// ─────────────────────────────────────────────────────────────

// Origen de la reserva → badge con color e icono propios.
const SOURCES = {
  web:    { label: 'Web',    icon: '🌐', color: '#4a8fa8' },
  nativa: { label: 'Nativa', icon: '🏠', color: '#4a7c59' },
  manual: { label: 'Manual', icon: '🏦', color: '#e8a430' },
};

// Etiqueta corta de la semana: "Semana 1" si el nombre lo contiene, si no el nombre.
function shortWeek(name) {
  const m = /Semana\s*\d+/i.exec(name || '');
  return m ? m[0] : (name || 'Semana');
}

// ── Exportación CSV ──────────────────────────────────────────
// Columnas del CSV en orden, con la cabecera legible en español y la clave del
// campo en cada fila de `campistas` que devuelve el backend.
const CSV_COLUMNS = [
  ['Semana',               'semana'],
  ['Origen',               'origen'],
  ['Reserva',              'reservaId'],
  ['Cliente',              'cliente'],
  ['Email contacto',       'emailContacto'],
  ['Nombre',               'nombre'],
  ['Apellido 1',           'apellido1'],
  ['Apellido 2',           'apellido2'],
  ['Año nacimiento',       'anioNacimiento'],
  ['Edad',                 'edad'],
  ['Sexo',                 'sexo'],
  ['Tutor',                'tutor'],
  ['Tel. tutor',           'telTutor'],
  ['Email tutor',          'emailTutor'],
  ['Alergias',             'alergias'],
  ['Medicación',           'medicacion'],
  ['Enfermedades',         'enfermedades'],
  ['Contacto emergencia',  'contactoEmergencia'],
  ['Tel. emergencia',      'telEmergencia'],
  ['Cesión datos',         'autorizaCesionDatos'],
  ['Imagen',               'autorizaImagen'],
  ['Medicación autoriz.',  'autorizaMedicacion'],
  ['Autobús',              'autobus'],
  ['Total',                'total'],
  ['Pagado',               'pagado'],
  ['Estado pago',          'estadoPago'],
];

// Escapa una celda para CSV con separador ';': si contiene ; " o salto de línea,
// se envuelve en comillas dobles y se duplican las comillas internas.
function csvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv(campistas) {
  const header = CSV_COLUMNS.map(([label]) => csvCell(label)).join(';');
  const rows = (campistas || []).map(c =>
    CSV_COLUMNS.map(([, key]) => csvCell(c[key])).join(';')
  );
  // BOM UTF-8 para que Excel muestre bien los acentos.
  return '﻿' + [header, ...rows].join('\r\n');
}

// Descarga el CSV vía Blob + <a> temporal.
function downloadCsv(csv, fileBase) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `campistas-${fileBase}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Normaliza un nombre de semana para usarlo en el nombre de fichero.
function slugFile(name) {
  return String(name || 'semana')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'semana';
}

function SourceBadge({ source }) {
  const s = SOURCES[source] || { label: source, icon: '⚪', color: '#8fa89f' };
  return (
    <span className="badge" style={{ background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}55` }}>
      {s.icon} {s.label}
    </span>
  );
}

// Indicador ✅ / 🟡 Pendiente para datos completos y pago.
function StatusIndicator({ ok, okLabel }) {
  if (ok) return <span style={{ color: 'var(--green-light)' }}>✅ {okLabel}</span>;
  return <span style={{ color: 'var(--warn)' }}>🟡 Pendiente</span>;
}

function money(n) { return `${(Number(n) || 0).toFixed(2)} €`; }

// Indicador del estado de autorización de imagen de los campistas de la reserva.
// f = { si, no, sin, total }.
function FotosBadge({ f }) {
  const base = { fontSize: '.76rem', fontWeight: 600, padding: '.12rem .45rem', borderRadius: 6, whiteSpace: 'nowrap' };
  if (!f || !f.total) {
    return <span style={{ ...base, color: 'var(--mid)' }} title="Sin datos de campistas todavía">❔ sin datos</span>;
  }
  const { si, no, sin, total } = f;
  if (no === 0 && sin === 0) {
    return <span style={{ ...base, color: 'var(--green-light)', background: 'rgba(90,122,80,.18)' }}
      title={total > 1 ? 'Todos autorizan fotos' : 'Autoriza fotos'}>✅ Fotos OK</span>;
  }
  if (si === 0 && sin === 0) {
    return <span style={{ ...base, color: '#e2796a', background: 'rgba(181,72,47,.2)' }}
      title={total > 1 ? 'Ninguno autoriza fotos' : 'No autoriza fotos'}>🚫 Sin fotos</span>;
  }
  const parts = [];
  if (si) parts.push(`✅${si}`);
  if (no) parts.push(`🚫${no}`);
  if (sin) parts.push(`❔${sin}`);
  return <span style={{ ...base, color: 'var(--warn)', background: 'rgba(232,164,48,.15)' }}
    title={`Autorizan ${si} · no ${no} · sin dato ${sin} (de ${total})`}>📷 {parts.join(' ')}</span>;
}

function ReservaRow({ r, onEnviar, enviando, onUpdatePago, pagoBusy, onEnviarImagen, imgEnviando }) {
  // Ajustar precio, marcar pagado y enviar link de pago: web, manual y nativas.
  // (Cada origen guarda en su sitio; el padre enruta según r.source.)
  const editable = (r.source === 'web' || r.source === 'manual' || r.source === 'nativa');
  const [editandoPrecio, setEditandoPrecio] = useState(false);
  const [precio, setPrecio] = useState(String(r.total ?? 0));
  const remaining = Math.max(0, (Number(r.total) || 0) - (Number(r.paid) || 0));

  return (
    <div className="card" style={{ padding: '.8rem 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <SourceBadge source={r.source} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ color: 'var(--white)', fontWeight: 600 }}>{r.name || '(sin nombre)'}</div>
          <div style={{ color: 'var(--mid)', fontSize: '.82rem' }}>{r.email || '—'}</div>
          {Array.isArray(r.campistas) && r.campistas.length > 0 ? (
            <div style={{
              display: 'flex', flexWrap: 'wrap',
              color: 'var(--mid)', fontSize: '.8rem', marginTop: '.2rem',
            }}>
              🧒&nbsp;{r.campistas.join(' · ')}
            </div>
          ) : (
            <div style={{ color: 'var(--mid)', fontSize: '.8rem', marginTop: '.2rem', fontStyle: 'italic' }}>
              Campistas sin datos aún
            </div>
          )}
        </div>
        <div style={{ color: 'var(--mid)', fontSize: '.82rem', textAlign: 'center', minWidth: 90 }}>
          <div style={{ color: 'var(--white)', fontWeight: 600 }}>👤 {r.campers != null ? r.campers : 0}</div>
          <div>campista(s)</div>
        </div>
        <div style={{ fontSize: '.82rem', textAlign: 'right', minWidth: 130 }}>
          <div><span style={{ color: 'var(--mid)' }}>Datos: </span><StatusIndicator ok={r.datosCompletos} okLabel="Completos" /></div>
          <div><span style={{ color: 'var(--mid)' }}>Pago: </span><StatusIndicator ok={r.pagado} okLabel="Pagado" /></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
          <FotosBadge f={r.fotos} />
          <button className="btn btn-ghost btn-sm"
            title="Enviar enlace para que la familia edite la autorización de imagen"
            disabled={imgEnviando}
            onClick={() => onEnviarImagen(r)}>
            {imgEnviando ? <span className="spinner" /> : '📷 Autoriz. imagen'}
          </button>
        </div>
      </div>

      {/* Bloque de pago (web/manual): importes, ajustar precio, marcar pagado y link de pago */}
      {editable && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap',
          marginTop: '.7rem', paddingTop: '.6rem', borderTop: '1px solid rgba(255,255,255,.08)',
        }}>
          {r.pagado ? (
            <span style={{ color: 'var(--green-light)', fontWeight: 600 }}>✅ Pagado · {money(r.total)}</span>
          ) : (
            <>
              <div style={{ fontSize: '.82rem', color: 'var(--mid)', display: 'flex', alignItems: 'center', gap: '.35rem', flexWrap: 'wrap' }}>
                <span>Total:</span>
                {editandoPrecio ? (
                  <input type="number" min="0" step="0.01" value={precio}
                    onChange={e => setPrecio(e.target.value)}
                    style={{
                      width: 90, padding: '.25rem .4rem', borderRadius: 6,
                      border: '1px solid rgba(255,255,255,.18)', background: 'rgba(0,0,0,.25)',
                      color: 'var(--white)', fontSize: '.85rem',
                    }} />
                ) : (
                  <strong style={{ color: 'var(--white)' }}>{money(r.total)}</strong>
                )}
                <span>· Pagado: {money(r.paid)} ·</span>
                <span style={{ color: 'var(--warn)' }}>Pendiente: {money(remaining)}</span>
              </div>
              {editandoPrecio ? (
                <>
                  <button className="btn btn-primary btn-sm" disabled={pagoBusy}
                    onClick={async () => { await onUpdatePago(r, { total: Number(precio) }); setEditandoPrecio(false); }}>
                    {pagoBusy ? <span className="spinner" /> : '💾 Guardar'}
                  </button>
                  <button className="btn btn-ghost btn-sm" disabled={pagoBusy}
                    onClick={() => { setPrecio(String(r.total ?? 0)); setEditandoPrecio(false); }}>
                    Cancelar
                  </button>
                </>
              ) : (
                <button className="btn btn-ghost btn-sm" disabled={pagoBusy}
                  onClick={() => { setPrecio(String(r.total ?? 0)); setEditandoPrecio(true); }}>
                  ✏️ Ajustar precio
                </button>
              )}
              <button className="btn btn-ghost btn-sm" disabled={pagoBusy}
                title="Marcar esta reserva como pagada por completo"
                onClick={() => onUpdatePago(r, { pagoCompletado: true }, true)}>
                {pagoBusy ? <span className="spinner" /> : '✅ Marcar pagado'}
              </button>
              <button className="btn btn-ghost btn-sm" disabled={enviando}
                title="Enviar email con el link de pago (resto + bus)"
                onClick={() => onEnviar(r)}>
                {enviando ? <span className="spinner" /> : '✉️ Enviar link de pago'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminRoster() {
  const { token } = useAuth();
  const [semanas, setSemanas] = useState([]);
  const [semanaSel, setSemanaSel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportando, setExportando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviandoId, setEnviandoId] = useState(null);
  const [pagoBusyId, setPagoBusyId] = useState(null);
  const [imgEnviandoId, setImgEnviandoId] = useState(null);
  const [imgEnviandoSemana, setImgEnviandoSemana] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.getAdminRoster(token);
      const sems = r.semanas || [];
      setSemanas(sems);
      if (sems.length > 0) setSemanaSel(sems[0].event.id);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el roster.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const activa = semanas.find(s => s.event.id === semanaSel) || null;

  // Exporta a CSV. Si `event` viene, solo esa semana; si no, todas.
  const exportar = useCallback(async (event) => {
    setExportando(true);
    try {
      const r = await api.getRosterExport(token, event ? event.id : undefined);
      const campistas = r.campistas || [];
      if (campistas.length === 0) {
        alert('No hay campistas para exportar.');
        return;
      }
      const csv = buildCsv(campistas);
      const fileBase = event ? slugFile(event.name) : 'todas-las-semanas';
      downloadCsv(csv, fileBase);
    } catch (e) {
      alert(e.message || 'No se pudo exportar el CSV.');
    } finally {
      setExportando(false);
    }
  }, [token]);

  // Envío masivo a la semana activa: a todas las reservas web/manual que aún
  // tengan algo pendiente (datos o pago). El email lleva al flujo /completar.
  const enviarSemana = useCallback(async () => {
    if (!activa) return;
    const pendientes = (activa.reservas || []).filter(r =>
      (r.source === 'web' || r.source === 'manual') && !(r.datosCompletos && r.pagado));
    const n = pendientes.length;
    if (n === 0) {
      alert('No hay reservas pendientes a las que enviar el email en esta semana.');
      return;
    }
    if (!window.confirm(`¿Enviar el email (completar datos / pagar resto + bus) a las ${n} reservas pendientes de ${activa.event.name}?`)) return;
    setEnviando(true);
    try {
      const res = await api.sendMotopressEmailMasivo(token, semanaSel);
      alert(`Enviados ${res.enviados} de ${res.total}.`);
      await load();
    } catch (e) {
      alert(e.message || 'No se pudieron enviar los emails.');
    } finally {
      setEnviando(false);
    }
  }, [activa, token, semanaSel, load]);

  // Envío individual del link de pago. Web/manual → email /completar; nativa → link de pago nativo.
  const enviarUna = useCallback(async (r) => {
    if (!window.confirm(`¿Enviar el link de pago a ${r.email || r.name}?`)) return;
    setEnviandoId(r.id);
    try {
      const res = r.source === 'nativa'
        ? await api.sendNativePayLinkEmail(token, r.id)
        : await api.sendMotopressEmail(token, r.id);
      alert(res?.message || `Email enviado a ${r.email}.`);
    } catch (e) {
      alert(e.message || 'No se pudo enviar el email.');
    } finally {
      setEnviandoId(null);
    }
  }, [token]);

  // Envía a UNA familia el enlace para editar la autorización de imagen.
  const enviarImagenUna = useCallback(async (r) => {
    const source = r.source === 'nativa' ? 'nativa' : 'web';
    if (!window.confirm(`¿Enviar a ${r.email || r.name} el enlace para gestionar la autorización de imagen?`)) return;
    setImgEnviandoId(r.id);
    try {
      const res = await api.sendImgConsentEmail(token, source, r.id);
      alert(res.message || 'Email enviado.');
    } catch (e) {
      alert(e.message || 'No se pudo enviar el email.');
    } finally {
      setImgEnviandoId(null);
    }
  }, [token]);

  // Envía el enlace de autorización de imagen a TODA la semana activa.
  const enviarImagenSemana = useCallback(async () => {
    if (!activa) return;
    if (!window.confirm(`¿Enviar el enlace de autorización de imagen a todas las familias de ${activa.event.name}?`)) return;
    setImgEnviandoSemana(true);
    try {
      const res = await api.sendImgConsentEmailMasivo(token, semanaSel);
      alert(`Enlaces enviados: ${res.enviados} de ${res.total}.`);
    } catch (e) {
      alert(e.message || 'No se pudieron enviar los enlaces.');
    } finally {
      setImgEnviandoSemana(false);
    }
  }, [activa, token, semanaSel]);

  // Ajusta el precio (total) o marca como pagada una reserva. Enruta según origen:
  // nativa → bookings; web/manual → motopress. body: { total } | { pagoCompletado } | { paid }.
  const actualizarPago = useCallback(async (r, body, confirmPagado = false) => {
    if (confirmPagado && body.pagoCompletado) {
      if (!window.confirm(`¿Marcar como PAGADA la reserva de ${r.name || r.email}? Quedará registrada como pago completado.`)) return;
    }
    setPagoBusyId(r.id);
    try {
      if (r.source === 'nativa') await api.updateNativePago(token, r.id, body);
      else                        await api.updateMotopressPago(token, r.id, body);
      await load();
    } catch (e) {
      alert(e.message || 'No se pudo actualizar el pago.');
    } finally {
      setPagoBusyId(null);
    }
  }, [token, load]);

  return (
    <div className="main-content page-enter">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.6rem', marginBottom: '1rem' }}>
        <div>
          <div className="section-label">Roster por semana</div>
          <h1 style={{ fontSize: '1.4rem', color: 'var(--white)', margin: 0 }}>Todas las reservas por semana de campamento</h1>
          <p style={{ color: 'var(--mid)', fontSize: '.85rem', marginTop: '.3rem' }}>
            Junta en una sola vista las reservas 🌐 web, 🏠 nativas y 🏦 manuales de cada semana.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => exportar(null)} disabled={loading || exportando || semanas.length === 0}>
            {exportando ? '⏳ Exportando…' : '⬇️ Exportar todas'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>↻ Actualizar</button>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}><span className="spinner spinner-lg" /></div>
      ) : semanas.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--mid)' }}>
          No hay semanas de campamento todavía.
        </div>
      ) : (
        <>
          {/* Selector de semana */}
          <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {semanas.map(s => (
              <button key={s.event.id}
                className={`btn btn-sm ${semanaSel === s.event.id ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setSemanaSel(s.event.id)}>
                📅 {shortWeek(s.event.name)}
              </button>
            ))}
          </div>

          {activa && (
            <>
              {/* Acciones de la semana activa */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.4rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
                <button className="btn btn-ghost btn-sm"
                  onClick={enviarSemana}
                  disabled={enviando}>
                  {enviando ? <><span className="spinner" /> Enviando…</> : '✉️ Enviar emails pendientes (datos/pago)'}
                </button>
                <button className="btn btn-ghost btn-sm"
                  onClick={enviarImagenSemana}
                  disabled={imgEnviandoSemana}>
                  {imgEnviandoSemana ? <><span className="spinner" /> Enviando…</> : '📷 Enviar autoriz. imagen (semana)'}
                </button>
                <button className="btn btn-primary btn-sm"
                  onClick={() => exportar(activa.event)}
                  disabled={exportando}>
                  {exportando ? '⏳ Exportando…' : '⬇️ Exportar campistas (CSV)'}
                </button>
              </div>

              {/* Tarjetas de resumen */}
              <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                {[
                  { label: 'Total',        icon: '',   value: activa.counts.total },
                  { label: 'Web',          icon: '🌐', value: activa.counts.web },
                  { label: 'Nativas',      icon: '🏠', value: activa.counts.nativas },
                  { label: 'Manuales',     icon: '🏦', value: activa.counts.manuales },
                  { label: 'Campistas',    icon: '👤', value: activa.counts.campers },
                ].map((c, i) => (
                  <div key={i} className="card" style={{ padding: '.7rem 1rem', minWidth: 120 }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--white)' }}>{c.value != null ? c.value : 0}</div>
                    <div style={{ fontSize: '.78rem', color: 'var(--mid)' }}>{c.icon ? `${c.icon} ` : ''}{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Lista de reservas de la semana activa */}
              {(activa.reservas || []).length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--mid)' }}>
                  Esta semana no tiene reservas todavía.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                  {activa.reservas.map(r => (
                    <ReservaRow key={`${r.source}-${r.id}`} r={r}
                      onEnviar={enviarUna} enviando={enviandoId === r.id}
                      onUpdatePago={actualizarPago} pagoBusy={pagoBusyId === r.id}
                      onEnviarImagen={enviarImagenUna} imgEnviando={imgEnviandoId === r.id} />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
